import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCopyToClipboard } from '../components/CopyButton';
import { Layout } from '../components/Layout';
import { Dropdown } from '../components/Dropdown';
import { Modal } from '../components/Modal';
import { ExportModal } from '../components/ExportModal';
import { ImportModal } from '../components/ImportModal';
import { WalletSelector, type WalletAuthResult } from '../components/WalletSelector';
import { TrialBanner } from '../components/TrialBanner';
import { Button } from '../components/ui';
import { GearIcon, BellIcon, ForwardIcon, CloseIcon } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import {
  getAccounts,
  getAccount,
  saveAccount,
  getMessages,
  hashPassword,
  verifyPassword,
  getActiveAccountSubdomain,
  setActiveAccountSubdomain,
  getSettings,
  saveSettings,
} from '../services/storage';
import { validateSelfSovereignConfig, testBeeConnection, testEthereumRpc } from '../services/storage/validation';
import { generateKeyPair, bytesToHex, hexToBytes } from '../services/swarm/encryption';
import { pollInbox, decryptMetadata } from '../services/swarm/gsoc';
import { requestAccountStamp, getStampInfo } from '../services/swarm/stamps';
import { registerAccount, type RegistrationStep } from '../services/account/registration';
import { RegistrationProgress } from '../components/RegistrationProgress';
import type { StampInfo } from '../types';
import { migratePublicKeyIfNeeded } from '../services/ens';
import { addMessage } from '../services/storage';
import { receiveAnonymousFile } from '../services/swarm/honestInbox';
import { triggerDownload } from '../services/swarm/download';
import {
  getKeypairBalance,
  sendFromKeypair,
  SUPPORTED_CHAINS,
  DEFAULT_ESCROW_CHAIN_ID,
} from '../services/wallet/keypair-wallet';
import type { Account, Message } from '../types';
import { validateUsername as validateUsernameFormat } from '../constants/reserved-names';
import { fdsIdEnabled, getFdsIdClient } from '../services/fds-id/client';

type TabType = 'received' | 'sent' | 'stored';

// Format file size for display
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Format timestamp for display
function formatTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return new Date(timestamp).toLocaleDateString();
}

export function Account() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInbox = location.pathname.includes('/account/');
  const { login: authLogin, logout: authLogout } = useAuth();

  // Resume active session synchronously to avoid login form flash
  const resumedAccount = (() => {
    const sub = getActiveAccountSubdomain();
    return sub ? getAccount(sub) ?? null : null;
  })();

  // Account list state
  const [mailboxOptions, setMailboxOptions] = useState<{ value: string; label: string }[]>([]);

  // Login state
  const [selectedMailbox, setSelectedMailbox] = useState('');
  const [newMailboxName, setNewMailboxName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(!!resumedAccount);
  const [loginError, setLoginError] = useState('');
  const [loginStatus, setLoginStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep | null>(null);

  // Current account state
  const [currentAccount, setCurrentAccount] = useState<Account | null>(resumedAccount);

  const isNewMailbox = selectedMailbox === 'new';

  // Inbox state
  const [activeTab, setActiveTab] = useState<TabType>('received');
  const [showSettings, setShowSettings] = useState(location.pathname === '/settings');
  const [messages, setMessages] = useState<Message[]>([]);
  const [downloadingRef, setDownloadingRef] = useState<string | null>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  // Settings state - Bee URL dropdown
  const BEE_URL_PRESETS = [
    { value: 'https://fairdrop.xyz/api/swarm', label: 'Fairdrop Server' },
    { value: 'https://xdai.fairdatasociety.org', label: 'FDS Public Gateway (xdai.fairdatasociety.org)' },
    { value: 'http://localhost:1633', label: 'Localhost (localhost:1633)' },
  ];
  const [selectedBeeUrl, setSelectedBeeUrl] = useState('');
  const [customBeeUrl, setCustomBeeUrl] = useState('');
  const [customBeeUrls, setCustomBeeUrls] = useState<string[]>([]);
  const isCustomUrl = selectedBeeUrl === 'custom';

  // Self-sovereign mode state
  const [selfSovereignMode, setSelfSovereignMode] = useState(false);
  const [ethereumRpcUrl, setEthereumRpcUrl] = useState('');
  const [defaultStampId, setDefaultStampId] = useState('');
  const [selfSovereignTestStatus, setSelfSovereignTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [selfSovereignTestMessage, setSelfSovereignTestMessage] = useState('');

  // Postage stamp state
  const [stampLoading, setStampLoading] = useState(false);
  const [stampError, setStampError] = useState<string | null>(null);
  const [stampInfo, setStampInfoState] = useState<StampInfo | null>(null);
  const [stampRetryCount, setStampRetryCount] = useState(0);
  const [requestingStamp, setRequestingStamp] = useState(false);
  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  // Export/Import modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  // Wallet sign-in modal state
  const [showWalletSelector, setShowWalletSelector] = useState(false);
  // Wallet username selection state
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [pendingWalletAuth, setPendingWalletAuth] = useState<WalletAuthResult | null>(null);
  const [walletUsername, setWalletUsername] = useState('');
  const [walletUsernameError, setWalletUsernameError] = useState('');
  // Optional password for wallet accounts
  const [walletPassword, setWalletPassword] = useState('');
  const [walletPasswordConfirm, setWalletPasswordConfirm] = useState('');
  const [showWalletPassword, setShowWalletPassword] = useState(false);
  // Copy feedback
  const { copy, isCopied } = useCopyToClipboard();
  // Inbox sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [braveBlockingWarning, setBraveBlockingWarning] = useState(false);
  // Wallet state (for vanilla keypairs)
  const [walletBalance, setWalletBalance] = useState<string | null>(null);
  const [_walletBalanceLoading, setWalletBalanceLoading] = useState(false);
  const [walletChainId, _setWalletChainId] = useState(DEFAULT_ESCROW_CHAIN_ID);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');


  // Load settings on mount
  useEffect(() => {
    const settings = getSettings();
    const savedUrl = settings.beeUrl || '';
    const savedCustomUrls = settings.customBeeUrls || [];
    setCustomBeeUrls(savedCustomUrls);

    // Check if saved URL matches a preset
    const isPreset = BEE_URL_PRESETS.some(p => p.value === savedUrl);
    if (savedUrl && !isPreset) {
      // It's a custom URL
      setSelectedBeeUrl('custom');
      setCustomBeeUrl(savedUrl);
    } else {
      setSelectedBeeUrl(savedUrl || BEE_URL_PRESETS[0].value);
    }

    // Load self-sovereign settings
    setSelfSovereignMode(settings.selfSovereign || false);
    setEthereumRpcUrl(settings.ethereumRpcUrl || '');
    setDefaultStampId(settings.defaultStampId || '');
  }, []);

  // Save Bee URL setting
  const handleSaveBeeUrl = () => {
    let urlToSave = selectedBeeUrl;
    let newCustomUrls = [...customBeeUrls];

    if (isCustomUrl && customBeeUrl.trim()) {
      urlToSave = customBeeUrl.trim();
      // Add to custom URLs if not already there
      if (!newCustomUrls.includes(urlToSave) && !BEE_URL_PRESETS.some(p => p.value === urlToSave)) {
        newCustomUrls.push(urlToSave);
        setCustomBeeUrls(newCustomUrls);
      }
    }

    saveSettings({ beeUrl: urlToSave, customBeeUrls: newCustomUrls });
    alert('Bee node URL saved. Changes will apply on next upload/download.');
  };

  // Save self-sovereign settings
  const handleSaveSelfSovereign = async () => {
    // Validate configuration
    const config = {
      selfSovereign: selfSovereignMode,
      beeUrl: selectedBeeUrl === 'custom' ? customBeeUrl : selectedBeeUrl,
      ethereumRpcUrl,
      defaultStampId,
    };

    const validation = validateSelfSovereignConfig(config);

    if (!validation.valid) {
      alert('Configuration errors:\n' + validation.errors.join('\n'));
      return;
    }

    if (validation.warnings.length > 0) {
      const proceed = confirm('Warnings:\n' + validation.warnings.join('\n') + '\n\nContinue anyway?');
      if (!proceed) return;
    }

    saveSettings({
      selfSovereign: selfSovereignMode,
      ethereumRpcUrl,
      defaultStampId,
    });

    alert('Self-sovereign settings saved.');
  };

  // Test self-sovereign connectivity
  const handleTestSelfSovereign = async () => {
    setSelfSovereignTestStatus('testing');
    setSelfSovereignTestMessage('Testing connectivity...');

    const beeUrl = selectedBeeUrl === 'custom' ? customBeeUrl : selectedBeeUrl;

    // Test Bee connection
    if (beeUrl) {
      const beeResult = await testBeeConnection(beeUrl);
      if (!beeResult.success) {
        setSelfSovereignTestStatus('error');
        setSelfSovereignTestMessage(`Bee connection failed: ${beeResult.error}`);
        return;
      }
    }

    // Test Ethereum RPC
    if (ethereumRpcUrl) {
      const rpcResult = await testEthereumRpc(ethereumRpcUrl);
      if (!rpcResult.success) {
        setSelfSovereignTestStatus('error');
        setSelfSovereignTestMessage(`Ethereum RPC failed: ${rpcResult.error}`);
        return;
      }
    }

    setSelfSovereignTestStatus('success');
    setSelfSovereignTestMessage('All connections successful!');
  };

  // Fetch stamp info when settings panel opens
  useEffect(() => {
    if (showSettings && currentAccount?.stampId) {
      setStampLoading(true);
      setStampError(null);
      getStampInfo(currentAccount.stampId)
        .then(info => {
          if (info) {
            setStampInfoState(info);
          } else {
            // Live fetch failed - use stored account data as fallback
            if (currentAccount.stampExpiresAt) {
              // Use stored capacity if available, otherwise default to depth 20 (4GB)
              const totalCapacity = currentAccount.stampTotalCapacity || Math.pow(2, 20) * 4096;
              const remainingCapacity = currentAccount.stampCapacity || totalCapacity;
              // Calculate depth from totalCapacity: depth = log2(totalCapacity / 4096)
              const depth = currentAccount.stampTotalCapacity
                ? Math.round(Math.log2(currentAccount.stampTotalCapacity / 4096))
                : 20;
              setStampInfoState({
                batchID: currentAccount.stampId!,
                expiresAt: currentAccount.stampExpiresAt,
                remainingCapacity,
                usable: currentAccount.stampExpiresAt > Date.now(),
                depth,
                utilization: Math.round((1 - remainingCapacity / totalCapacity) * 100),
              });
            } else {
              setStampError('Stamp not found');
            }
          }
        })
        .catch(() => {
          // Network error - use stored account data as fallback
          if (currentAccount.stampExpiresAt) {
            // Use stored capacity if available, otherwise default to depth 20 (4GB)
            const totalCapacity = currentAccount.stampTotalCapacity || Math.pow(2, 20) * 4096;
            const remainingCapacity = currentAccount.stampCapacity || totalCapacity;
            const depth = currentAccount.stampTotalCapacity
              ? Math.round(Math.log2(currentAccount.stampTotalCapacity / 4096))
              : 20;
            setStampInfoState({
              batchID: currentAccount.stampId!,
              expiresAt: currentAccount.stampExpiresAt,
              remainingCapacity,
              usable: currentAccount.stampExpiresAt > Date.now(),
              depth,
              utilization: Math.round((1 - remainingCapacity / totalCapacity) * 100),
            });
          } else {
            setStampError('Unable to fetch stamp info');
          }
        })
        .finally(() => setStampLoading(false));
    } else if (showSettings && !currentAccount?.stampId) {
      // No stamp assigned
      setStampInfoState(null);
      setStampError(null);
    }
  }, [showSettings, currentAccount?.stampId, currentAccount?.stampExpiresAt, stampRetryCount]);

  // Fetch wallet balance when settings panel opens (for vanilla keypairs)
  useEffect(() => {
    if (showSettings && currentAccount?.privateKey && !currentAccount?.walletAddress) {
      // This is a vanilla keypair (no external wallet connected)
      setWalletBalanceLoading(true);
      getKeypairBalance(currentAccount.privateKey, walletChainId)
        .then(({ formatted }) => {
          setWalletBalance(formatted);
        })
        .catch(err => {
          logger.warn('[Mailbox] Failed to fetch wallet balance:', err);
          setWalletBalance(null);
        })
        .finally(() => setWalletBalanceLoading(false));
    }
  }, [showSettings, currentAccount?.privateKey, currentAccount?.walletAddress, walletChainId]);

  // Repair missing GSOC inbox params (best-effort, background)
  useEffect(() => {
    if (!currentAccount || currentAccount.inboxParams || !currentAccount.publicKey) return;
    if (!fdsIdEnabled()) return;

    let cancelled = false;
    const repair = async () => {
      try {
        const client = getFdsIdClient();
        logger.info('[Mailbox] Repairing missing GSOC inbox params for', currentAccount.subdomain);
        const result = await client.repairInbox(currentAccount.subdomain, currentAccount.publicKey!);
        if (cancelled) return;
        if (result.repaired && result.inbox) {
          const updated = { ...currentAccount, inboxParams: { ...result.inbox, recipientPublicKey: currentAccount.publicKey } };
          saveAccount(updated);
          setCurrentAccount(updated);
          logger.info('[Mailbox] GSOC inbox repaired for', currentAccount.subdomain);
        } else if (!result.repaired && result.inbox) {
          // Already set on ENS but missing locally — save locally
          const updated = { ...currentAccount, inboxParams: { ...result.inbox, recipientPublicKey: currentAccount.publicKey } };
          saveAccount(updated);
          setCurrentAccount(updated);
          logger.info('[Mailbox] GSOC inbox params synced from ENS for', currentAccount.subdomain);
        }
      } catch (err) {
        logger.warn('[Mailbox] GSOC inbox repair failed (non-fatal):', err);
      }
    };
    repair();
    return () => { cancelled = true; };
  }, [currentAccount?.subdomain, currentAccount?.inboxParams, currentAccount?.publicKey]);

  // Refresh wallet balance
  const handleRefreshBalance = async () => {
    if (!currentAccount?.privateKey) return;
    setWalletBalanceLoading(true);
    try {
      const { formatted } = await getKeypairBalance(currentAccount.privateKey, walletChainId);
      setWalletBalance(formatted);
    } catch (err) {
      logger.warn('[Mailbox] Failed to fetch wallet balance:', err);
    } finally {
      setWalletBalanceLoading(false);
    }
  };

  // Handle withdraw
  const handleWithdraw = async () => {
    if (!currentAccount?.privateKey || !withdrawAddress || !withdrawAmount) return;

    // Validate address
    if (!withdrawAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      setWithdrawError('Invalid Ethereum address');
      return;
    }

    // Validate amount
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      setWithdrawError('Invalid amount');
      return;
    }

    setWithdrawing(true);
    setWithdrawError('');

    try {
      const result = await sendFromKeypair(
        currentAccount.privateKey,
        withdrawAddress,
        withdrawAmount,
        walletChainId
      );

      // Success - close modal and refresh balance
      setShowWithdrawModal(false);
      setWithdrawAddress('');
      setWithdrawAmount('');
      handleRefreshBalance();

      // Show success with link to explorer
      window.open(result.explorerUrl, '_blank');
    } catch (err) {
      setWithdrawError(err instanceof Error ? err.message : 'Transaction failed');
    } finally {
      setWithdrawing(false);
    }
  };

  // Get current chain info
  const currentChain = SUPPORTED_CHAINS.find(c => c.id === walletChainId);

  // Close settings panel (navigate away if on /settings route)
  const handleCloseSettings = () => {
    setShowSettings(false);
    if (location.pathname === '/settings') {
      navigate('/account');
    }
  };

  // Retry fetching stamp info
  const handleRetryStampInfo = () => setStampRetryCount(c => c + 1);

  // Copy address to clipboard
  const handleCopyAddress = () => {
    if (!currentAccount?.publicKey) return;
    copy(`0x${currentAccount.publicKey}`, 'address');
  };

  // Copy stamp ID to clipboard
  const handleCopyStamp = () => {
    if (!stampInfo?.batchID) return;
    copy(stampInfo.batchID, 'stamp');
  };

  // Open stamp management page to buy/top up stamps (pre-fill stamp ID)
  const handleOpenStampSite = () => {
    const stampId = currentAccount?.stampId;
    const url = stampId
      ? `https://stamp.fairdatasociety.org/?stamp=${stampId}`
      : 'https://stamp.fairdatasociety.org/';
    window.open(url, '_blank');
  };

  // Password change handler
  const handlePasswordChange = async () => {
    if (!currentAccount) return;

    // Validate inputs
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setPasswordChangeError('All fields are required');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordChangeError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordChangeError('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    setPasswordChangeError('');

    try {
      // Verify old password
      const isValid = await verifyPassword(oldPassword, currentAccount.passwordHash);
      if (!isValid) {
        setPasswordChangeError('Current password is incorrect');
        setChangingPassword(false);
        return;
      }

      // Hash new password and update account
      const newPasswordHash = await hashPassword(newPassword);
      const updatedAccount: Account = {
        ...currentAccount,
        passwordHash: newPasswordHash,
      };
      saveAccount(updatedAccount);
      setCurrentAccount(updatedAccount);

      // Clear form and close modal
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowPasswordModal(false);
    } catch (error) {
      setPasswordChangeError('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Reset password modal state when closing
  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordChangeError('');
  };

  // Handle wallet authentication success
  const handleWalletAuth = async (result: WalletAuthResult) => {
    logger.debug('[Mailbox] Wallet authenticated:', result.address);
    setLoginError('');

    // Check if any account already exists with this wallet address
    const accounts = getAccounts();
    const existingAccount = Object.values(accounts).find(
      acc => acc.walletAddress?.toLowerCase() === result.address.toLowerCase()
    );

    if (existingAccount) {
      // Existing wallet account - just log in
      logger.debug('[Mailbox] Existing wallet account found:', existingAccount.subdomain);
      authLogin(existingAccount.subdomain);
      setCurrentAccount(existingAccount);
      setActiveAccountSubdomain(existingAccount.subdomain);
      setIsLoggedIn(true);
    } else {
      // New wallet - show username selection modal
      logger.debug('[Mailbox] New wallet, showing username selection');
      setPendingWalletAuth(result);
      setWalletUsername('');
      setWalletUsernameError('');
      setShowUsernameModal(true);
    }
  };

  // Handle username submission for new wallet accounts
  const handleWalletUsernameSubmit = async () => {
    if (!pendingWalletAuth) return;

    const username = walletUsername.trim().toLowerCase();

    // Validate username format and reserved names
    const validation = validateUsernameFormat(username);
    if (!validation.valid) {
      setWalletUsernameError(validation.error || 'Invalid username');
      return;
    }

    // Check if username already exists locally
    const existing = getAccount(username);
    if (existing) {
      setWalletUsernameError('This username is already taken');
      return;
    }

    // Validate password if provided
    if (showWalletPassword) {
      if (walletPassword.length < 6) {
        setWalletUsernameError('Password must be at least 6 characters');
        return;
      }
      if (walletPassword !== walletPasswordConfirm) {
        setWalletUsernameError('Passwords do not match');
        return;
      }
    }

    setIsLoading(true);
    setShowUsernameModal(false);

    try {
      // Generate encryption keypair
      const keyPair = generateKeyPair();
      const publicKeyHex = bytesToHex(keyPair.publicKey);

      // Hash password if provided (enables export/cross-device access)
      const passwordHash = showWalletPassword && walletPassword
        ? await hashPassword(walletPassword)
        : '';

      // Create account with chosen username
      const newAccount: Account = {
        subdomain: username,
        publicKey: publicKeyHex,
        privateKey: bytesToHex(keyPair.privateKey),
        passwordHash, // Empty if no password (device-specific account)
        created: Date.now(),
        walletAddress: pendingWalletAuth.address,
      };

      // Update dropdown options
      setMailboxOptions(prev => [...prev, { value: username, label: username }]);

      // Register via orchestrator (fds-id primary, fallback to local)
      const effectivePassword = showWalletPassword && walletPassword ? walletPassword : '';
      const result = await registerAccount(
        username,
        publicKeyHex,
        bytesToHex(keyPair.privateKey),
        effectivePassword,
        { onStatus: (status) => setLoginStatus(status) },
        { walletAddress: pendingWalletAuth.address, walletProvider: pendingWalletAuth.provider }
      );

      if (result.warnings.length > 0) {
        logger.warn('[Mailbox] Registration warnings:', result.warnings);
      }

      // Log in
      setLoginStatus('');
      authLogin(username);
      setCurrentAccount(getAccount(username) || newAccount);
      setActiveAccountSubdomain(username);
      setIsLoggedIn(true);
      setPendingWalletAuth(null);
      // Clear password state
      setWalletPassword('');
      setWalletPasswordConfirm('');
      setShowWalletPassword(false);
    } catch (error) {
      logger.error('[Mailbox] Wallet account creation error:', error);
      setLoginError('Failed to create account. Please try again.');
      setPendingWalletAuth(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Request stamp for legacy accounts
  const handleRequestStamp = async () => {
    if (!currentAccount) return;
    setRequestingStamp(true);
    setStampError(null);
    try {
      const stamp = await requestAccountStamp(currentAccount.privateKey, currentAccount.subdomain);
      const updatedAccount: Account = {
        ...currentAccount,
        stampId: stamp.batchId,
        stampExpiresAt: stamp.expiresAt,
        stampCapacity: stamp.remainingCapacity,
        stampTotalCapacity: stamp.totalCapacity,
      };
      saveAccount(updatedAccount);
      setCurrentAccount(updatedAccount);
      setStampRetryCount(c => c + 1); // Trigger refresh
    } catch (error) {
      setStampError('Failed to request stamp. Try again later.');
    } finally {
      setRequestingStamp(false);
    }
  };

  // Format bytes to human readable
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format expiry time
  const formatExpiry = (timestamp: number, usable?: boolean) => {
    if (!timestamp) return usable ? 'Active' : 'Unknown';
    const now = Date.now();
    const diff = timestamp - now;
    if (diff <= 0) return 'Expired';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  // Load accounts from storage on mount
  useEffect(() => {
    const accounts = getAccounts();
    const options = Object.keys(accounts).map(subdomain => ({
      value: subdomain,
      label: subdomain,
    }));
    setMailboxOptions(options);

    // Pre-select active account (session already resumed via useState initializers)
    const activeSubdomain = getActiveAccountSubdomain();
    if (activeSubdomain && accounts[activeSubdomain]) {
      setSelectedMailbox(activeSubdomain);
      authLogin(activeSubdomain);
    }
  }, []);

  // Migrate public key if needed (for legacy accounts with null publicKey in ENS)
  useEffect(() => {
    if (currentAccount?.publicKey && currentAccount?.subdomain) {
      migratePublicKeyIfNeeded(currentAccount.subdomain, currentAccount.publicKey)
        .then(result => {
          if (result.migrated) {
            logger.debug(`[Mailbox] Public key migrated for ${currentAccount.subdomain}`);
          }
        })
        .catch(err => logger.warn('[Mailbox] Migration check failed:', err));
    }
  }, [currentAccount?.subdomain, currentAccount?.publicKey]);

  // Load messages when account changes or tab changes
  useEffect(() => {
    let isMounted = true;

    if (currentAccount) {
      // Load local messages first
      const localMsgs = getMessages(currentAccount.subdomain, activeTab);
      setMessages(localMsgs);

      // For received tab, also poll GSOC for new messages
      if (activeTab === 'received' && currentAccount.inboxParams) {
        logger.debug('[Mailbox] Polling GSOC for new messages...');

        // Wrap in async IIFE with full error handling
        (async () => {
          try {
            // Use higher maxScan for thorough poll when no local cache exists
            // This ensures imported accounts or first-time logins find all messages
            const isThoroughPoll = localMsgs.length === 0;
            const maxScan = isThoroughPoll ? 100 : 20;

            // Show syncing indicator for thorough polls (after import)
            if (isThoroughPoll) {
              setIsSyncing(true);
              setSyncResult(null);
            }

            logger.debug('[Mailbox] Using maxScan:', maxScan, isThoroughPoll ? '(thorough poll)' : '(incremental poll)');
            const { messages: gsocMessages, networkErrors } = await pollInbox(currentAccount.inboxParams!, 0, maxScan);

            // Check if component is still mounted before updating state
            if (!isMounted) return;

            logger.debug('[Mailbox] GSOC poll returned', gsocMessages.length, 'messages,', networkErrors, 'network errors');

            if (networkErrors > 0 && gsocMessages.length === 0) {
              setBraveBlockingWarning(true);
            } else {
              setBraveBlockingWarning(false);
            }

            if (gsocMessages.length > 0) {
              // Convert GSOC messages to our Message format and save
              // Decrypt sender info if available
              const newMessages: Message[] = await Promise.all(
                gsocMessages.map(async (gsocMsg) => {
                  let from = 'Anonymous';
                  let filename = gsocMsg.filename || 'Anonymous file';

                  // Try to decrypt sender metadata if present
                  if (gsocMsg.encryptedMeta && currentAccount.privateKey) {
                    try {
                      const privateKeyBytes = hexToBytes(currentAccount.privateKey);
                      const senderInfo = await decryptMetadata(gsocMsg.encryptedMeta, privateKeyBytes);
                      from = senderInfo.from || 'Anonymous';
                      filename = senderInfo.filename || filename;
                      logger.debug('[Mailbox] Decrypted sender info:', from);
                    } catch (decryptErr) {
                      logger.warn('[Mailbox] Failed to decrypt sender metadata:', decryptErr);
                    }
                  }

                  return {
                    reference: gsocMsg.reference,
                    filename,
                    size: gsocMsg.size || 0,
                    from,
                    timestamp: gsocMsg.timestamp,
                    encrypted: true,
                  };
                })
              );

              // Save new messages to storage
              newMessages.forEach((msg) => {
                addMessage(currentAccount.subdomain, 'received', msg);
              });

              // Update state with combined messages (check mounted again)
              if (!isMounted) return;
              const updatedMsgs = getMessages(currentAccount.subdomain, 'received');
              setMessages(updatedMsgs);
              logger.debug('[Mailbox] Updated inbox with', updatedMsgs.length, 'total messages');

              // Show notification if not on received tab
              if (activeTab !== 'received') {
                setHasNewMessages(true);
              }

              // Show sync result for thorough polls
              if (isThoroughPoll) {
                setSyncResult(`Found ${newMessages.length} message${newMessages.length !== 1 ? 's' : ''}`);
              }
            } else if (isThoroughPoll) {
              setSyncResult('No messages found');
            }
          } catch (error) {
            logger.error('[Mailbox] GSOC poll failed:', error);
            setSyncResult('Sync failed - try refreshing');
          } finally {
            if (isMounted) {
              setIsSyncing(false);
              // Clear sync result after 5 seconds
              setTimeout(() => {
                if (isMounted) setSyncResult(null);
              }, 5000);
            }
          }
        })();
      } else if (activeTab === 'received' && !currentAccount.inboxParams) {
        logger.debug('[Mailbox] No inbox params - cannot poll GSOC');
      }
    }

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [currentAccount, activeTab]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);

    try {
      if (isNewMailbox) {
        // Creating new mailbox
        const mailboxName = newMailboxName.trim().toLowerCase();

        if (!mailboxName) {
          setLoginError('Please enter a username');
          setIsLoading(false);
          return;
        }

        if (mailboxName.length < 3) {
          setLoginError('Username must be at least 3 characters');
          setIsLoading(false);
          return;
        }

        // Check if already exists
        const existing = getAccount(mailboxName);
        if (existing) {
          setLoginError('An account with this name already exists');
          setIsLoading(false);
          return;
        }

        if (password.length < 6) {
          setLoginError('Password must be at least 6 characters');
          setIsLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setLoginError('Passwords do not match');
          setIsLoading(false);
          return;
        }

        // Generate keypair for encryption
        const keyPair = generateKeyPair();
        const publicKeyHex = bytesToHex(keyPair.publicKey);

        // Update dropdown options
        setMailboxOptions(prev => [...prev, { value: mailboxName, label: mailboxName }]);

        // Register via orchestrator (fds-id primary, fallback to local)
        setRegistrationStep('authenticating');
        const result = await registerAccount(
          mailboxName,
          publicKeyHex,
          bytesToHex(keyPair.privateKey),
          password,
          {
            onStatus: (status) => setLoginStatus(status),
            onStep: (step) => setRegistrationStep(step),
          }
        );

        if (result.warnings.length > 0) {
          logger.warn('[Mailbox] Registration warnings:', result.warnings);
        }

        // Now login after registration is complete
        setLoginStatus('');
        setRegistrationStep(null);
        authLogin(mailboxName);
        setCurrentAccount(getAccount(mailboxName) || result.account);
        setActiveAccountSubdomain(mailboxName);
        setIsLoggedIn(true);
        logger.debug('[Mailbox] Account ready, entering inbox');
      } else {
        // Logging into existing mailbox
        if (!selectedMailbox) {
          setLoginError('Please select an account');
          setIsLoading(false);
          return;
        }

        const account = getAccount(selectedMailbox);
        if (!account) {
          setLoginError('Account not found');
          setIsLoading(false);
          return;
        }

        const isValid = await verifyPassword(password, account.passwordHash);
        if (!isValid) {
          setLoginError('Incorrect password');
          setIsLoading(false);
          return;
        }

        authLogin(selectedMailbox);
        setCurrentAccount(account);
        setActiveAccountSubdomain(account.subdomain);
        setIsLoggedIn(true);
      }
    } catch (error) {
      logger.error('Login error:', error);
      setLoginError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
      setRegistrationStep(null);
      setPassword('');
      setConfirmPassword('');
    }
  };

  const handleLogout = () => {
    setCurrentAccount(null);
    setActiveAccountSubdomain(null);
    setIsLoggedIn(false);
    setMessages([]);
    authLogout();
    setPassword('');
    navigate('/');
  };

  // Handle download with decryption for received files
  const handleDownload = async (message: Message) => {
    if (!currentAccount?.privateKey) {
      logger.error('[Mailbox] No private key available for decryption');
      return;
    }

    setDownloadingRef(message.reference);
    logger.debug('[Mailbox] Downloading and decrypting:', message.filename);

    try {
      // Download and decrypt the file
      const decrypted = await receiveAnonymousFile(message.reference, currentAccount.privateKey);
      logger.debug('[Mailbox] File decrypted, size:', decrypted.file.size);

      // Convert Blob to Uint8Array for triggerDownload
      const arrayBuffer = await decrypted.file.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);

      // Trigger browser download with correct filename
      triggerDownload(data, message.filename, decrypted.metadata.type);
      logger.debug('[Mailbox] Download triggered');
    } catch (error) {
      logger.error('[Mailbox] Download failed:', error);
      // Could show error to user here
    } finally {
      setDownloadingRef(null);
    }
  };

  // Show login form if not logged in (or if on inbox route without account data)
  if (!isLoggedIn && (!isInbox || !currentAccount)) {
    return (
      <Layout theme="white">
        <div className="content-centered">
          <h1 className="heading-lg" style={{ color: 'var(--text-primary)', marginBottom: 40 }}>
            {registrationStep ? 'Creating your account' : 'Log In / Register'}
          </h1>

          <form onSubmit={handleLogin} style={{ width: '100%', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
            {/* Form elements - hidden during wallet registration */}
            <div className={`login-form-fields ${(isLoading && loginStatus) || registrationStep ? 'hidden' : ''}`}>
              <div className="form-group">
                {isNewMailbox ? (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="login-input"
                      placeholder="Enter username"
                      value={newMailboxName}
                      onChange={e => setNewMailboxName(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMailbox('');
                        setNewMailboxName('');
                      }}
                      style={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        fontSize: 18,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <Dropdown
                    options={[
                      ...mailboxOptions,
                      { value: 'new', label: 'new account +' },
                      { value: 'import', label: 'import from backup...' },
                    ]}
                    value={selectedMailbox}
                    onChange={(value) => {
                      if (value === 'import') {
                        setShowImportModal(true);
                        setSelectedMailbox('');
                      } else {
                        setSelectedMailbox(value);
                      }
                    }}
                    placeholder="Select account"
                  />
                )}
              </div>

              <div className="form-group">
                <input
                  type="password"
                  className="login-input"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  aria-describedby={isNewMailbox ? 'password-hint' : undefined}
                />
                {isNewMailbox && (
                  <p id="password-hint" style={{ fontSize: 12, opacity: 0.7, marginTop: 4, marginBottom: 0 }}>
                    Minimum 6 characters
                  </p>
                )}
              </div>

              <div
                className="form-group"
                style={{
                  maxHeight: isNewMailbox ? 60 : 0,
                  opacity: isNewMailbox ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  marginBottom: isNewMailbox ? 16 : 0,
                }}
              >
                <input
                  type="password"
                  className="login-input"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            {loginError && (
              <p style={{ color: '#e74c3c', marginTop: 8, fontSize: 14 }}>{loginError}</p>
            )}

            {registrationStep ? (
              <RegistrationProgress
                currentStep={registrationStep}
                username={newMailboxName.trim().toLowerCase()}
              />
            ) : loginStatus ? (
              <p className="login-status-message">{loginStatus}</p>
            ) : null}

            <Button type="submit" variant="primary" style={{ marginTop: 24 }} disabled={isLoading}>
              {isLoading ? 'Please wait...' : isNewMailbox ? 'Create Account' : 'Sign In'}
            </Button>

            {/* "or" and wallet button - hidden during wallet registration */}
            <div className={`login-wallet-section ${(isLoading && loginStatus) || registrationStep ? 'hidden' : ''}`}>
              <div style={{ marginTop: 24, textAlign: 'center' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>or</span>
              </div>

              <Button
                type="button"
                variant="outline"
                style={{ marginTop: 16 }}
                onClick={() => setShowWalletSelector(true)}
                disabled={isLoading}
              >
                Sign in with Wallet
              </Button>
            </div>
          </form>

          {/* Wallet Selector Modal */}
          <WalletSelector
            isOpen={showWalletSelector}
            onClose={() => setShowWalletSelector(false)}
            onSuccess={handleWalletAuth}
            onError={(error) => {
              logger.error('[Mailbox] Wallet connection error:', error);
              setLoginError(error.message);
            }}
          />

          {/* Wallet Username Modal */}
          <Modal
            isOpen={showUsernameModal}
            onClose={() => {
              setShowUsernameModal(false);
              setPendingWalletAuth(null);
              setWalletPassword('');
              setWalletPasswordConfirm('');
              setShowWalletPassword(false);
            }}
            title="Choose Your Username"
          >
            <div className="wallet-selector-username">
              <div className="wallet-selector-username-header">
                <p>This username will be your Fairdrop address</p>
              </div>

              <input
                type="text"
                className="wallet-selector-username-input"
                placeholder="Enter username"
                value={walletUsername}
                onChange={(e) => {
                  setWalletUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                  setWalletUsernameError('');
                }}
                autoFocus
              />

              <p className="wallet-selector-username-hint">
                Lowercase letters, numbers, and hyphens only. Min 3 characters.
              </p>

              {/* Optional password toggle */}
              <div className="wallet-password-toggle">
                <label className="wallet-password-toggle-label">
                  <input
                    type="checkbox"
                    checked={showWalletPassword}
                    onChange={(e) => {
                      setShowWalletPassword(e.target.checked);
                      if (!e.target.checked) {
                        setWalletPassword('');
                        setWalletPasswordConfirm('');
                      }
                    }}
                  />
                  <span>Add password for cross-device access</span>
                </label>
              </div>

              {/* Password fields (shown when toggle is on) */}
              {showWalletPassword && (
                <div className="wallet-password-fields">
                  <input
                    type="password"
                    className="wallet-selector-username-input"
                    placeholder="Password (min 6 characters)"
                    value={walletPassword}
                    onChange={(e) => setWalletPassword(e.target.value)}
                  />
                  <input
                    type="password"
                    className="wallet-selector-username-input"
                    placeholder="Confirm password"
                    value={walletPasswordConfirm}
                    onChange={(e) => setWalletPasswordConfirm(e.target.value)}
                  />
                </div>
              )}

              {/* Device-only warning when no password */}
              {!showWalletPassword && (
                <div className="wallet-device-warning">
                  <span className="wallet-device-warning-icon">⚠</span>
                  <span>Without a password, this account is device-specific and cannot be exported.</span>
                </div>
              )}

              {walletUsernameError && (
                <p className="wallet-selector-username-error">{walletUsernameError}</p>
              )}

              <div className="wallet-selector-username-actions">
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    setShowUsernameModal(false);
                    setPendingWalletAuth(null);
                    setWalletPassword('');
                    setWalletPasswordConfirm('');
                    setShowWalletPassword(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-white"
                  onClick={handleWalletUsernameSubmit}
                  disabled={!walletUsername || walletUsername.length < 3}
                >
                  Create Account
                </button>
              </div>
            </div>
          </Modal>

          {/* Import Modal */}
          <ImportModal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            onImportSuccess={(account) => {
              // Add to mailbox options if not already there
              setMailboxOptions(prev => {
                const exists = prev.some(opt => opt.value === account.subdomain);
                if (exists) return prev;
                return [...prev, { value: account.subdomain, label: account.subdomain }];
              });
              // Log in with imported account
              setCurrentAccount(account);
              setIsLoggedIn(true);
            }}
          />
        </div>
      </Layout>
    );
  }

  // Inbox view
  const tabs: { key: TabType; label: string; icon: string }[] = [
    { key: 'received', label: 'Received', icon: '✓' },
    { key: 'sent', label: 'Sent', icon: '→' },
    { key: 'stored', label: 'Stored', icon: '⬡' },
  ];

  const getEmptyMessage = () => {
    switch (activeTab) {
      case 'received':
        return { title: 'No received files yet', subtitle: 'Files sent to you will appear here' };
      case 'sent':
        return { title: 'No sent files yet', subtitle: 'Files you send will appear here' };
      case 'stored':
        return { title: 'No stored files yet', subtitle: 'Files you store will appear here' };
    }
  };

  const headerRight = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <span className="inbox-badge">{currentAccount?.subdomain || 'Unknown'}</span>
      <button
        onClick={handleLogout}
        className="inbox-logout"
      >
        Log out
      </button>
      <button
        className={`inbox-icon-btn ${hasNewMessages ? 'has-notification' : ''}`}
        title="Notifications"
        onClick={() => {
          setActiveTab('received');
          setHasNewMessages(false);
        }}
      >
        <BellIcon />
      </button>
      <button className="inbox-icon-btn" title="Settings" onClick={() => setShowSettings(true)}>
        <GearIcon />
      </button>
    </div>
  );

  return (
    <Layout theme="white" headerRight={headerRight}>
      {/* Trial Expiry Banner */}
      <TrialBanner account={currentAccount} onOpenSettings={() => setShowSettings(true)} />

      {/* Settings Panel Overlay */}
      <div
        className={`settings-overlay ${showSettings ? 'open' : ''}`}
        onClick={handleCloseSettings}
      />

      {/* Settings Panel */}
      <div className={`settings-panel ${showSettings ? 'open' : ''}`}>
        <div className="settings-panel-header">
          <h2>Settings</h2>
          <button className="settings-panel-close" onClick={handleCloseSettings}>
            <CloseIcon />
          </button>
        </div>

        <div className="settings-panel-content">
          <div className="settings-section">
            <h3>Account</h3>
            <div className="settings-item">
              <div className="settings-item-info">
                <span className="settings-item-label">{currentAccount?.subdomain || 'Unknown'}</span>
                <span
                  className="settings-item-desc"
                  style={{ fontFamily: 'monospace', fontSize: 9, cursor: 'pointer', wordBreak: 'break-all' }}
                  onClick={handleCopyAddress}
                  title="Click to copy public key"
                >
                  {currentAccount?.publicKey
                    ? `0x${currentAccount.publicKey}`
                    : 'No address'}
                  {isCopied('address') && <span style={{ marginLeft: 6, color: '#27ae60' }}>Copied!</span>}
                </span>
              </div>
            </div>
          </div>

          {/* Wallet/Escrow section hidden in production - escrow only available via MCP for now */}
          {/* TODO: Re-enable when escrow contract is deployed to mainnet */}

          <div className="settings-section">
            <h3>Postage Stamp</h3>
            {stampLoading ? (
              <div className="settings-item">
                <div className="settings-item-info">
                  <span className="settings-item-label">Loading...</span>
                  <span className="settings-item-desc">Fetching stamp info</span>
                </div>
              </div>
            ) : stampError ? (
              <div className="settings-item">
                <div className="settings-item-info">
                  <span className="settings-item-label" style={{ color: '#e74c3c' }}>⚠ {stampError}</span>
                </div>
                <button className="settings-item-action" onClick={handleRetryStampInfo}>Retry</button>
              </div>
            ) : !currentAccount?.stampId ? (
              <div className="settings-item">
                <div className="settings-item-info">
                  <span className="settings-item-label">No stamp assigned</span>
                  <span className="settings-item-desc">Request a free stamp to upload files</span>
                </div>
                <button
                  className="settings-item-action"
                  onClick={handleRequestStamp}
                  disabled={requestingStamp}
                >
                  {requestingStamp ? 'Requesting...' : 'Request Free Stamp'}
                </button>
              </div>
            ) : stampInfo ? (
              <>
                <div className="settings-item">
                  <div className="settings-item-info">
                    <span className="settings-item-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: stampInfo.usable ? '#27ae60' : '#e74c3c' }}>●</span>
                      {stampInfo.depth > 0 && stampInfo.utilization < 90 ? (
                        // Show capacity only if we have real depth and meaningful utilization
                        <>{formatBytes(stampInfo.remainingCapacity)} free</>
                      ) : (
                        // Sponsored stamp or high utilization - just show status
                        <>{stampInfo.usable ? 'Active' : 'Expired'}</>
                      )}
                      {' · '}{formatExpiry(stampInfo.expiresAt, stampInfo.usable)}
                    </span>
                    <span
                      className="settings-item-desc"
                      style={{ fontFamily: 'monospace', fontSize: 10, cursor: 'pointer', wordBreak: 'break-all' }}
                      onClick={handleCopyStamp}
                      title="Click to copy stamp ID"
                    >
                      {stampInfo.batchID.slice(0, 12)}...{stampInfo.batchID.slice(-12)}
                      {isCopied('stamp') && <span style={{ marginLeft: 6, color: '#27ae60' }}>Copied!</span>}
                    </span>
                  </div>
                  <button
                    className="settings-item-action"
                    onClick={handleOpenStampSite}
                    title="Top up your stamp on stamp.fairdatasociety.org"
                  >
                    Top up
                  </button>
                </div>
              </>
            ) : (
              <div className="settings-item">
                <div className="settings-item-info">
                  <span className="settings-item-label">Loading stamp info...</span>
                </div>
              </div>
            )}
          </div>

          <div className="settings-section">
            <h3>Security</h3>
            <div className="settings-item">
              <div className="settings-item-info">
                <span className="settings-item-label">Change Password</span>
                <span className="settings-item-desc">Update your account password</span>
              </div>
              <button className="settings-item-action" onClick={() => setShowPasswordModal(true)}>Change</button>
            </div>
            <div className="settings-item">
              <div className="settings-item-info">
                <span className="settings-item-label">Export Account</span>
                <span className="settings-item-desc">Download encrypted backup or save to cloud</span>
              </div>
              <button className="settings-item-action" onClick={() => setShowExportModal(true)}>Export</button>
            </div>
          </div>

          <div className="settings-section">
            <h3>Network</h3>
            <div className="settings-item">
              <div className="settings-item-info">
                <span className="settings-item-label">Bee Node URL</span>
                <span className="settings-item-desc">Swarm node for uploads/downloads</span>
              </div>
            </div>
            <div className="settings-item" style={{ marginTop: 4, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ width: isCustomUrl ? '40%' : '70%' }}>
                <Dropdown
                  options={[
                    ...BEE_URL_PRESETS,
                    ...customBeeUrls.map(url => ({ value: url, label: url })),
                    { value: 'custom', label: 'Custom URL...' },
                  ]}
                  value={selectedBeeUrl}
                  onChange={setSelectedBeeUrl}
                  placeholder="Select Bee node"
                />
              </div>
              {isCustomUrl && (
                <input
                  type="text"
                  className="settings-input"
                  placeholder="https://your-bee-node.com"
                  value={customBeeUrl}
                  onChange={e => setCustomBeeUrl(e.target.value)}
                  style={{ width: 'calc(30% - 8px)', padding: '6px 12px', fontSize: 13 }}
                />
              )}
              <button className="settings-item-action" onClick={handleSaveBeeUrl}>
                Save
              </button>
            </div>
          </div>

          <div className="settings-section">
            <h3>Self-Sovereign Mode</h3>
            <div className="settings-item">
              <div className="settings-item-info">
                <span className="settings-item-label">Enable Self-Sovereign Mode</span>
                <span className="settings-item-desc">Use your own infrastructure instead of FDS servers</span>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={selfSovereignMode}
                  onChange={(e) => setSelfSovereignMode(e.target.checked)}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            {selfSovereignMode && (
              <>
                <div className="settings-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <div className="settings-item-info">
                    <span className="settings-item-label">Ethereum RPC URL</span>
                    <span className="settings-item-desc">For ENS operations (optional, uses public RPC as fallback)</span>
                  </div>
                  <input
                    type="text"
                    className="settings-input"
                    placeholder="https://eth.llamarpc.com"
                    value={ethereumRpcUrl}
                    onChange={e => setEthereumRpcUrl(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 13 }}
                  />
                </div>

                <div className="settings-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <div className="settings-item-info">
                    <span className="settings-item-label">Default Stamp ID</span>
                    <span className="settings-item-desc">Your postage stamp for uploads (64 hex characters)</span>
                  </div>
                  <input
                    type="text"
                    className="settings-input"
                    placeholder="abc123..."
                    value={defaultStampId}
                    onChange={e => setDefaultStampId(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'monospace' }}
                  />
                </div>

                <div className="settings-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  {selfSovereignTestStatus !== 'idle' && (
                    <div className={`settings-test-result ${selfSovereignTestStatus}`}>
                      {selfSovereignTestStatus === 'testing' && '⏳ '}
                      {selfSovereignTestStatus === 'success' && '✓ '}
                      {selfSovereignTestStatus === 'error' && '✗ '}
                      {selfSovereignTestMessage}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="settings-item-action" onClick={handleTestSelfSovereign}>
                      Test Connection
                    </button>
                    <button className="settings-item-action" onClick={handleSaveSelfSovereign}>
                      Save Settings
                    </button>
                  </div>
                </div>

                <div className="settings-item" style={{ marginTop: 8 }}>
                  <a
                    href="https://github.com/fairDataSociety/fds-id-ts/blob/main/docs/SELF-SOVEREIGN.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 13, opacity: 0.8 }}
                  >
                    Learn how to set up self-sovereign mode →
                  </a>
                </div>
              </>
            )}
          </div>

          <div className="settings-section settings-danger">
            <h3>Danger Zone</h3>
            <div className="settings-item disabled">
              <div className="settings-item-info">
                <span className="settings-item-label">Delete Account</span>
                <span className="settings-item-desc">Permanently delete account and all files</span>
              </div>
              <button className="settings-item-action danger">Delete</button>
            </div>
          </div>
        </div>
      </div>

      <div className="inbox-layout">
        {/* Sidebar */}
        <aside className="inbox-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                // Clear notification when switching to received tab
                if (tab.key === 'received') {
                  setHasNewMessages(false);
                }
              }}
              className={`inbox-nav-item ${activeTab === tab.key ? 'active' : ''}`}
            >
              <span className="inbox-nav-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </aside>

        {/* Main content */}
        <main className="inbox-main">
          {/* Sync status indicator */}
          {(isSyncing || syncResult) && (
            <div className={`inbox-sync-status ${isSyncing ? 'syncing' : ''}`}>
              {isSyncing ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                    <circle cx="12" cy="12" r="10" strokeDasharray="31.4" strokeDashoffset="10" />
                  </svg>
                  <span>Syncing inbox...</span>
                </>
              ) : (
                <span>{syncResult}</span>
              )}
            </div>
          )}

          {/* Table header */}
          <div className="inbox-table-header">
            <span className="inbox-col-name">Name</span>
            <span className="inbox-col-from">From</span>
            <span className="inbox-col-time">Time</span>
            <span className="inbox-col-size">Size</span>
            <span className="inbox-col-actions">Actions</span>
          </div>

          {/* Content */}
          {messages.length === 0 ? (
            <div className="inbox-empty">
              <p className="inbox-empty-title">{getEmptyMessage().title}</p>
              <p className="inbox-empty-subtitle">{getEmptyMessage().subtitle}</p>
              {activeTab === 'received' && braveBlockingWarning && (
                <p className="inbox-empty-subtitle" style={{ marginTop: 8, color: 'var(--color-warning, #d97706)' }}>
                  Some inbox requests were blocked — if you are using Brave, try disabling Shields for this site.
                </p>
              )}
            </div>
          ) : (
            <div className="inbox-table-body">
              {messages.map(message => (
                <div key={message.reference} className="inbox-table-row">
                  <span
                    className="inbox-col-name inbox-col-name-clickable"
                    onClick={() => handleDownload(message)}
                    title="Click to download"
                  >
                    {message.filename}
                  </span>
                  <span className="inbox-col-from">{message.from || '-'}</span>
                  <span className="inbox-col-time">{formatTime(message.timestamp)}</span>
                  <span className="inbox-col-size">{formatFileSize(message.size)}</span>
                  <span className="inbox-col-actions">
                    <button
                      className="inbox-action-btn"
                      title="Forward"
                      onClick={() => {
                        // Navigate to send page with the file reference pre-filled
                        navigate(`/flow?forward=${message.reference}&filename=${encodeURIComponent(message.filename)}`);
                      }}
                    >
                      <ForwardIcon />
                    </button>
                    <button
                      className="inbox-action-btn"
                      title={downloadingRef === message.reference ? 'Downloading...' : 'Download'}
                      onClick={() => handleDownload(message)}
                      disabled={downloadingRef === message.reference}
                    >
                      {downloadingRef === message.reference ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                          <circle cx="12" cy="12" r="10" strokeDasharray="31.4" strokeDashoffset="10" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      )}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Password Change Modal */}
      <Modal isOpen={showPasswordModal} onClose={handleClosePasswordModal} title="Change Password">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {passwordChangeError && (
            <div className="modal-error">{passwordChangeError}</div>
          )}
          <input
            type="password"
            placeholder="Current password"
            value={oldPassword}
            onChange={e => setOldPassword(e.target.value)}
            className="modal-input"
          />
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            className="modal-input"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmNewPassword}
            onChange={e => setConfirmNewPassword(e.target.value)}
            className="modal-input"
          />
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={handleClosePasswordModal}>
              Cancel
            </button>
            <button
              className="btn btn-white"
              onClick={handlePasswordChange}
              disabled={changingPassword}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Export Account Modal */}
      {currentAccount && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          account={currentAccount}
        />
      )}

      {/* Withdraw Modal */}
      <Modal
        isOpen={showWithdrawModal}
        onClose={() => {
          setShowWithdrawModal(false);
          setWithdrawAddress('');
          setWithdrawAmount('');
          setWithdrawError('');
        }}
        title={`Withdraw ${currentChain?.symbol || 'ETH'}`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {withdrawError && (
            <div className="modal-error">{withdrawError}</div>
          )}

          <div style={{ fontSize: 13, opacity: 0.8 }}>
            Network: <strong>{currentChain?.name}</strong>
            {currentChain?.isTestnet && <span style={{ marginLeft: 8, color: '#f39c12' }}>(testnet)</span>}
          </div>

          <div style={{ fontSize: 13 }}>
            Available: <strong>{walletBalance} {currentChain?.symbol || 'ETH'}</strong>
          </div>

          <input
            type="text"
            placeholder="Recipient address (0x...)"
            value={withdrawAddress}
            onChange={e => setWithdrawAddress(e.target.value)}
            className="modal-input"
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="number"
              placeholder="Amount"
              value={withdrawAmount}
              onChange={e => setWithdrawAmount(e.target.value)}
              className="modal-input"
              style={{ flex: 1 }}
              step="0.001"
              min="0"
            />
            <button
              type="button"
              onClick={() => {
                // Set max (leave some for gas)
                if (walletBalance) {
                  const maxAmount = Math.max(0, parseFloat(walletBalance) - 0.001);
                  setWithdrawAmount(maxAmount.toFixed(6));
                }
              }}
              style={{
                padding: '8px 12px',
                fontSize: 12,
                borderRadius: 4,
                border: '1px solid #ddd',
                background: '#f5f5f5',
                cursor: 'pointer',
              }}
            >
              Max
            </button>
          </div>

          <div className="modal-actions">
            <button
              className="btn btn-outline"
              onClick={() => {
                setShowWithdrawModal(false);
                setWithdrawAddress('');
                setWithdrawAmount('');
                setWithdrawError('');
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-white"
              onClick={handleWithdraw}
              disabled={withdrawing || !withdrawAddress || !withdrawAmount}
            >
              {withdrawing ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </Modal>

    </Layout>
  );
}
